import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import type { ITabItem } from '@/interface/Tab';
import { MarketTab } from '@/interface/enum';
import { i18n } from '@/libraries/i18n';
import useBetBuilderMatchExists from '@/libraries/oddsApi/composables/useBetBuilderMatchExists';
import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

export function useMarketTab(matchId: number, isBetBuilderQueryEnable: Ref<boolean> = ref(true), gaPrefix = 'MatchCard') {
    const marketTab = ref(MarketTab.Sbo);

    const { isBetBuilderExists } = useBetBuilderMatchExists(matchId, isBetBuilderQueryEnable);

    const marketTabs = computed((): ITabItem[] => {
        const result = [
            {
                title: i18n.t('market_tab_title_sbo').toString(),
                value: MarketTab.Sbo,
                tabGA: `${gaPrefix}_Btn_SBOMarket`,
                isEnabled: true,
            },
            {
                title: i18n.t('bet_builder').toString(),
                value: MarketTab.BetBuilder,
                tabGA: `${gaPrefix}_Btn_BetBuilder`,
                isEnabled: isBetBuilderExists.value,
            },
        ];
        return result.filter(x => x.isEnabled);
    });

    const { isBetBuilderEnabled } = storeToRefs(useFeatureToggleStore());
    const isDisplayBetBuilder = computed(() => marketTab.value === MarketTab.BetBuilder && isBetBuilderExists.value && isBetBuilderEnabled.value);

    const { setTopScreenToast } = useRootStore();
    watch(isBetBuilderExists, async (isExists) => {
        if (!isExists) {
            const { betSlip, removeBetSlip } = useBetBuilderBetSlipStore();
            if (betSlip.tickets.length > 0) {
                setTopScreenToast({
                    status: 'error',
                    messageKey: 'betSlip_errorMessage_unavailable',
                });
                await removeBetSlip();
            }
        }
    });

    watch(isDisplayBetBuilder, (value) => {
        if (!value) {
            marketTab.value = MarketTab.Sbo;
        }
    });

    function setMarketTab(tab: MarketTab) {
        marketTab.value = tab;
    }

    return {
        marketTab,
        setMarketTab,
        marketTabs,
        isDisplayBetBuilder,
        isBetBuilderExists,
    };
}
